import { authHeaders } from "./firebase";
import type {
  ChatMessage,
  ChatReply,
  CloudMemorySnapshot,
  PendingAttachment,
  PersistedState,
  ResponsePreference,
  UserProfile,
} from "../types";

function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

async function authenticatedFetch(path: string, init: RequestInit, retry = true): Promise<Response> {
  const headers = await authHeaders(!retry);
  const response = await fetch(path, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...headers,
      ...(init.headers ?? {}),
    },
  });
  if ((response.status === 401 || response.status === 403) && retry) {
    return authenticatedFetch(path, init, false);
  }
  return response;
}

function errorForStatus(status: number, message?: string): Error {
  if (status === 401 || status === 403) return new Error("Your session expired. Sign in again to continue.");
  if (status === 413) return new Error("That screenshot is too large. Choose a smaller image and try again.");
  if (status === 429) return new Error(message ?? "You’re moving quickly. Give it a moment, then try again.");
  if (status === 504) return new Error("Pearlie took too long to respond. Try again.");
  return new Error(message ?? "Pearlie is briefly unavailable. Try again shortly.");
}

export async function sendChat(options: {
  message: string;
  requestID?: string;
  profile: UserProfile;
  messages: ChatMessage[];
  notebook: Record<string, unknown> | null;
  lastMessageAt: string | null;
  attachment?: PendingAttachment;
  preference: ResponsePreference;
  opening?: boolean;
  requestSponsoredSuggestion?: boolean;
  gravitySessionID: string;
  research?: {
    enabled: boolean;
    conversationID: string;
    deletionKey: string;
  };
}): Promise<ChatReply> {
  const recentMessages = options.messages
    .filter(message => !message.excludedFromMemory)
    .slice(-80)
    .map(message => ({
      role: message.role === "pearlie" ? "assistant" : "user",
      content: message.content,
      ...(message.visualContext ? { visualContext: message.visualContext } : {}),
    }));

  const response = await authenticatedFetch("/api/chat", {
    method: "POST",
    body: JSON.stringify({
      ...(options.requestID ? { requestId: options.requestID } : {}),
      message: options.message.trim(),
      responsePreference: options.opening ? undefined : options.preference,
      profile: {
        name: options.profile.name,
        age: options.profile.age,
        currentSituations: options.profile.currentSituations,
        lifeStory: options.profile.lifeStory,
        goals: options.profile.goals,
        pressures: options.profile.pressures,
        preferredTone: options.profile.preferredTone,
        alwaysRemember: options.profile.alwaysRemember,
        dependents: options.profile.dependents,
        whyNow: options.profile.whyNow,
      },
      recentMessages,
      ...(options.attachment ? {
        attachment: {
          mimeType: options.attachment.mimeType,
          base64Data: options.attachment.base64Data,
        },
      } : {}),
      ...(options.notebook ? { notebook: options.notebook } : {}),
      clientTime: new Date().toISOString(),
      clientTimeZone: browserTimeZone(),
      ...(options.lastMessageAt ? { lastMessageAt: options.lastMessageAt } : {}),
      ...(options.opening ? { opening: true } : {}),
      // Which voice answers. Requests without one get the voice from before
      // the app became Pearlie, which old iPhone installs still use.
      persona: "pearlie",
      ...(options.requestSponsoredSuggestion ? {
        requestSponsoredSuggestion: true,
        gravitySessionId: options.gravitySessionID,
      } : {}),
      ...(!options.opening && !options.attachment && options.research?.enabled ? {
        research: {
          consent: true,
          conversationId: options.research.conversationID,
          deletionKey: options.research.deletionKey,
          platform: "web",
        },
      } : {}),
    }),
  });

  if (!response.ok) {
    const detail = await response.json().catch(() => ({})) as { message?: string };
    throw errorForStatus(response.status, detail.message);
  }
  return response.json() as Promise<ChatReply>;
}

export async function sendResearchFeedback(requestID: string, feedback: "helpful" | "notQuite"): Promise<void> {
  const response = await authenticatedFetch("/api/research-feedback", {
    method: "POST",
    body: JSON.stringify({ requestId: requestID, feedback }),
  });
  if (!response.ok) throw errorForStatus(response.status);
}

export async function deleteSharedResearch(deletionKey: string): Promise<void> {
  const response = await authenticatedFetch("/api/research", {
    method: "DELETE",
    body: JSON.stringify({ deletionKey }),
  });
  if (!response.ok) throw errorForStatus(response.status);
}

export async function downloadCloudMemory(): Promise<CloudMemorySnapshot | null> {
  const collected: ChatMessage[] = [];
  let offset: number | undefined;
  let latest: CloudMemorySnapshot | null = null;
  do {
    // persona=pearlie asks for replies labelled "pearlie"; without it the
    // backup answers in the old app's terms.
    const path = offset ? `/api/memory?persona=pearlie&offset=${offset}` : "/api/memory?persona=pearlie";
    const response = await authenticatedFetch(path, { method: "GET" });
    if (response.status === 204 || response.status === 403) return null;
    if (!response.ok) throw errorForStatus(response.status);
    latest = await response.json() as CloudMemorySnapshot;
    collected.push(...latest.messages.map(message => ({ ...message, role: message.role === "user" ? "user" as const : "pearlie" as const })));
    offset = latest.nextOffset;
  } while (offset);

  return latest ? { ...latest, messages: collected, nextOffset: undefined } : null;
}

export async function uploadCloudMemory(state: PersistedState): Promise<void> {
  if (!state.profile) return;
  const snapshot: CloudMemorySnapshot = {
    version: 1,
    profile: state.profile,
    messages: state.messages.slice(-250).map(({ sponsoredAd: _ad, ...message }) => message),
    ...(state.notebook ? { notebook: state.notebook } : {}),
    ...(state.lastMessageAt ? { lastMessageAt: state.lastMessageAt } : {}),
    updatedAt: new Date().toISOString(),
  };
  const response = await authenticatedFetch("/api/memory", {
    method: "PUT",
    body: JSON.stringify(snapshot),
  });
  if (response.status === 403) return;
  if (!response.ok) throw errorForStatus(response.status);
}

export async function deleteCloudMemory(): Promise<void> {
  const response = await authenticatedFetch("/api/memory", { method: "DELETE" });
  if (response.status === 403 || response.status === 204) return;
  if (!response.ok) throw errorForStatus(response.status);
}

export async function compressScreenshot(file: File): Promise<PendingAttachment> {
  if (!file.type.startsWith("image/")) throw new Error("Choose a screenshot or image file.");
  const bitmap = await createImageBitmap(file);
  const maxDimension = 1_800;
  const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Pearlie couldn’t prepare that screenshot.");
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  const blob = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(value => value ? resolve(value) : reject(new Error("Pearlie couldn’t prepare that screenshot.")), "image/jpeg", 0.82)
  );
  if (blob.size > 3_000_000) throw new Error("That screenshot is too large. Crop it and try again.");
  const dataURL = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Pearlie couldn’t read that screenshot."));
    reader.readAsDataURL(blob);
  });
  return {
    fileName: file.name,
    mimeType: "image/jpeg",
    base64Data: dataURL.split(",")[1] ?? "",
    previewURL: dataURL,
  };
}
